// The geometry of "Show me around": the hole in the dimmed screen around a control, and where the caption card
// goes so it never covers that control. Plain numbers in screen pixels, so it can be tested without a browser.

export interface Box { x: number; y: number; w: number; h: number }
export interface Insets { top: number; right: number; bottom: number; left: number }

/** The hole around a control: a little larger than it, and as round as it (a pill around a pill). */
export function holeAround(r: Box, pad: number): Box & { radius: number } {
  return { x: r.x - pad, y: r.y - pad, w: r.w + 2 * pad, h: r.h + 2 * pad, radius: Math.min(r.w, r.h) / 2 + pad };
}

/**
 * Where the caption card goes: on the side of the control with more of the screen (above the dock, below the top
 * bar), unless it only fits on the other side; across, as near the control as the screen allows. With nothing to
 * point at, it sits in the middle. `maxH` is all the room it has there, for a card that has to scroll (large text
 * on a phone on its side).
 */
export function placeCard(
  target: Box | null,
  card: { w: number; h: number },
  view: { w: number; h: number },
  safe: Insets,
  gap = 12,
): { left: number; top: number; maxH: number; above: boolean } {
  const m = 12;
  const minTop = safe.top + m;
  const maxBottom = view.h - safe.bottom - m;
  const minLeft = safe.left + m;
  const maxLeft = Math.max(minLeft, view.w - safe.right - m - card.w);
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
  if (!target) {
    const maxH = Math.max(0, maxBottom - minTop);
    const h = Math.min(card.h, maxH);
    return { left: clamp((view.w - card.w) / 2, minLeft, maxLeft), top: Math.round(minTop + (maxH - h) / 2), maxH, above: false };
  }
  const roomAbove = Math.max(0, target.y - gap - minTop);
  const roomBelow = Math.max(0, maxBottom - (target.y + target.h + gap));
  let above = target.y + target.h / 2 > view.h / 2;
  // The preferred side is too small for the card while the other side is bigger: use the other side.
  if (above && card.h > roomAbove && roomBelow > roomAbove) above = false;
  else if (!above && card.h > roomBelow && roomAbove > roomBelow) above = true;
  const maxH = above ? roomAbove : roomBelow;
  const h = Math.min(card.h, maxH);
  const top = above ? target.y - gap - h : target.y + target.h + gap;
  const left = clamp(target.x + target.w / 2 - card.w / 2, minLeft, maxLeft);
  return { left: Math.round(left), top: Math.round(top), maxH: Math.round(maxH), above };
}
